import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

// Hook for pull-to-refresh functionality
export function usePullToRefresh(onRefresh, options = {}) {
  const { threshold = 80, resistance = 2.5 } = options;
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);

  const handleTouchStart = useCallback((e) => {
    // Only trigger if we're at the top of the page
    if (window.scrollY === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const diff = (currentY.current - startY.current) / resistance;
    
    if (diff > 0 && window.scrollY === 0) {
      setPullDistance(Math.min(diff, threshold * 1.5));
      // Prevent default scrolling when pulling
      if (diff > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, isRefreshing, resistance, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold / 2); // Keep showing indicator
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setPullDistance(0);
    setIsPulling(false);
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh]);

  return {
    pullDistance,
    isRefreshing,
    isPulling,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    threshold,
  };
}

// Visual component for pull-to-refresh indicator
export function PullToRefreshIndicator({ pullDistance, isRefreshing, threshold }) {
  const progress = Math.min(pullDistance / threshold, 1);
  const shouldShow = pullDistance > 10 || isRefreshing;
  
  if (!shouldShow) return null;
  
  return (
    <div 
      className="fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-200 pointer-events-none"
      style={{ 
        top: `${Math.min(pullDistance, threshold)}px`,
        opacity: progress,
        transform: `translateX(-50%) scale(${0.5 + progress * 0.5})`
      }}
    >
      <div className={`
        bg-white rounded-full shadow-lg p-3 border
        ${isRefreshing ? 'border-bronze' : 'border-slate-200'}
      `}>
        {isRefreshing ? (
          <Loader2 className="w-5 h-5 text-bronze animate-spin" />
        ) : (
          <ArrowDown 
            className={`w-5 h-5 transition-transform duration-200 ${
              progress >= 1 ? 'text-bronze rotate-180' : 'text-slate-400'
            }`}
          />
        )}
      </div>
    </div>
  );
}

// Wrapper component that provides pull-to-refresh
export default function PullToRefresh({ children, onRefresh, className = '' }) {
  const { pullDistance, isRefreshing, handlers, threshold } = usePullToRefresh(onRefresh);
  
  // Check if we're on a touch device
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);
  
  if (!isTouchDevice) {
    return <div className={className}>{children}</div>;
  }
  
  return (
    <div 
      className={className}
      {...handlers}
      style={{ touchAction: pullDistance > 10 ? 'none' : 'auto' }}
    >
      <PullToRefreshIndicator 
        pullDistance={pullDistance} 
        isRefreshing={isRefreshing} 
        threshold={threshold}
      />
      <div 
        style={{ 
          transform: pullDistance > 0 ? `translateY(${pullDistance * 0.3}px)` : 'none',
          transition: pullDistance === 0 ? 'transform 0.2s ease-out' : 'none'
        }}
      >
        {children}
      </div>
    </div>
  );
}
